import React, { FC, Suspense, useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Auth, Hub } from 'aws-amplify';
import { HubPayload } from '@aws-amplify/core';
import { useAppDispatch, useAppSelector } from 'redux/store';
import toast from 'components/Base/Notification';
import authSlice from 'redux/slices/auth.slice';
import { EVia } from 'data/types/amplify.types';
import { currentAuthUserConvertor } from 'data/convertors/amplify.convertors';
import { oppLocalStorage, TStorageNames } from 'helpers/storage';
import useInvitedAccountLink from 'hooks/useInvitedAccountLink';
import { publicRoutes } from './RoutesData';
import { RoutesPaths } from './Routes.types';

export const PublicRoutes: FC<{
  isLoggedIn: boolean;
  isOnboarding: boolean;
}> = ({ isLoggedIn, isOnboarding }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signInUser = useAppSelector(state => state.authSlice.signInUser);
  const dispatch = useAppDispatch();
  // PublicRoutes is not lazy, so this runs in the first commit — before the
  // lazily-loaded login page exists, and before any auth round-trip can clear
  // `amplifyUser` out from under the comparison. Recording it here is what lets
  // everything downstream still know a switch is under way.
  const {
    fromLink,
    outgoingFromSession,
    pending: switchingAccount,
    staleOffRoute,
  } = useInvitedAccountLink();

  // Record and release sit together on purpose: this is the only place that owns
  // the lifetime of the invited flag, so the two halves cannot drift apart.
  //
  // Both names are captured in this one commit. The outgoing one is needed later
  // than the invited one — to name the account in the confirmation prompt, and to
  // clear its cached tokens once the switch completes — by which time reading
  // `amplifyUser` again may answer nothing.
  useEffect(() => {
    if (fromLink) {
      dispatch(
        authSlice.actions.setInvitedAccountSwitch({
          invitedUsername: fromLink,
          outgoingUsername: outgoingFromSession,
        }),
      );
    }
  }, [fromLink]);

  // The visitor is on a public route that is neither /login nor /new-password, so
  // the invited sign-in was abandoned rather than completed. The guard is already
  // inert by route scope; this clears the sessionStorage mirror so returning to
  // /login later in the same tab is a fresh start.
  useEffect(() => {
    if (staleOffRoute) {
      dispatch(authSlice.actions.removeInvitedAccountSwitch());
    }
  }, [staleOffRoute]);

  const authListener = async ({ payload }: { payload: HubPayload }) => {
    const { event } = payload;
    const autoSignIn = oppLocalStorage.getItem(TStorageNames.amplifyAutoSignIn);
    if (event === 'autoSignIn' && autoSignIn) {
      const amplifyUserData = currentAuthUserConvertor.fromDb(payload.data);
      dispatch(authSlice.actions.setAmplifyUser(amplifyUserData));
      navigate(
        { pathname: `/${RoutesPaths.ONBOARDING}`, search: location.search },
        {
          state: { viaEmail: signInUser?.via === EVia.EMAIL },
        },
      );
      dispatch(authSlice.actions.removeSignInUser());
    } else if (
      // This condition will work when redirected from email confirmation button
      // or confirmation without autoSignIn - true
      event === 'autoSignIn_failure' ||
      (event === 'confirmSignUp' && !autoSignIn)
    ) {
      // Try to auto-sign-in with stored credentials
      if (signInUser?.username && signInUser?.password) {
        try {
          const user = await Auth.signIn(
            signInUser.username,
            signInUser.password,
          );
          const amplifyUserData = currentAuthUserConvertor.fromDb(user);
          dispatch(authSlice.actions.setAmplifyUser(amplifyUserData));
          navigate(
            { pathname: `/${RoutesPaths.ONBOARDING}`, search: location.search },
            { state: { viaEmail: signInUser?.via === EVia.EMAIL } },
          );
          dispatch(authSlice.actions.removeSignInUser());
          return;
        } catch (e) {
          // Fall through to manual login
        }
      }

      if (signInUser?.username) {
        searchParams.set('username', signInUser.username);
      }
      toast({
        type: 'success',
        description:
          'You have successfully confirmed your account. Please provide your credentials to continue.',
      });
      dispatch(authSlice.actions.removeSignInUser());
      searchParams.delete('verificationCode');
      navigate({
        pathname: `/${RoutesPaths.LOGIN}`,
        search: searchParams.toString(),
      });
    }
  };

  useEffect(() => {
    Hub.listen('auth', authListener);

    return () => {
      Hub.remove('auth', authListener);
    };
  }, [signInUser]);

  useEffect(() => {
    // An invitation link is signing in the account it names. Acting on the
    // outgoing session would hand the visitor back the unit they were already in,
    // which is how pets were filed against the wrong unit.
    if (switchingAccount) {
      return;
    }
    if (isLoggedIn) {
      navigate(`/app/${RoutesPaths.ANIMAL}`);
    }
    if (isOnboarding) {
      navigate(`/${RoutesPaths.ONBOARDING}`);
    }
  }, [isLoggedIn, isOnboarding, switchingAccount]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route
          path="/"
          element={<Navigate replace to={publicRoutes[0].path} />}
        />
        {publicRoutes
          .filter(item => import.meta.env.DEV || !item.onlyDev)
          .map(({ path, component }) => {
            const Component = component;
            return (
              <Route key={path} path={`${path}`} element={<Component />} />
            );
          })}
      </Routes>
    </Suspense>
  );
};
